import { digitalSaleState, PREORDER_NOTICE, saleFormats } from '@/lib/commerce/preorder-policy';
import type { TitleDoc } from '@/types/platform';

const normalized = (text?: string) => (text || '').trim().replace(/\s+/g, ' ');

/** De-duplicate presentation only; never rewrite the author's stored copy. */
export function bookIntroduction(title: TitleDoc) {
  const seen = new Set([normalized(title.title), normalized(title.description)]);
  const take = (text?: string) => {
    const key = normalized(text);
    if (!key || seen.has(key)) return undefined;
    seen.add(key);
    return text;
  };
  const subtitle = take(title.subtitle);
  const hook = take(title.marketingHook);
  const shortDescription = take(title.shortDescription);
  const audience = take(title.audienceLine);
  const bullets = (title.featureBullets || []).map(take).filter((text): text is string => Boolean(text));
  return { subtitle, hook, shortDescription, audience, bullets };
}

export function publicWebsiteHref(value?: string) {
  try {
    const url = new URL(value || '');
    return ['http:', 'https:'].includes(url.protocol) && !url.username && !url.password ? url.href : undefined;
  } catch { return undefined; }
}

export function bookPurchasePresentation(title: TitleDoc, checkoutAvailable: boolean) {
  const state = digitalSaleState(title);
  const formats = saleFormats(title);
  const isPublic = title.storefrontVisibility && ['published', 'approved'].includes(title.publicationStatus);
  const blocked = !isPublic ? 'Not available' : state === 'coming-soon' ? 'Coming soon'
    : !formats.length ? 'Digital edition unavailable'
    : !checkoutAvailable ? 'Checkout unavailable' : null;
  const notice = !isPublic ? 'This title is not available to purchase.' : state === 'coming-soon' ? 'Coming soon — purchases are not open yet.'
    : !formats.length ? (state === 'preorder' ? 'Pre-orders are not open for this title yet.' : 'This digital edition is not available to purchase yet. Please check back soon.')
    : !checkoutAvailable ? 'Online checkout is currently unavailable. Please check back soon.'
    : state === 'preorder' ? PREORDER_NOTICE
    : 'Your selected download will be available in your account library after verified purchase.';
  return {
    state, formats, blocked, notice,
    badge: state === 'preorder' ? 'Pre-order' : 'Digital edition',
    // Even while checkout is paused, explain the deferred delivery promise.
    deliveryNotice: state === 'preorder' && notice !== PREORDER_NOTICE ? `${notice} ${PREORDER_NOTICE}` : notice,
  };
}
